/** Boundary rules and the canonical restart clock. Source: referee.cpp:68-269. */

import { Vector3 } from 'three';
import { PITCH } from '../core/constants';
import { clamp } from '../core/math';
import { hashedRandom } from '../core/random';
import type { Match, MatchPlayer } from './match';
import { prepareSetPiece, SetPiece, type SetPieceSetup } from './setpiece';
import { offsideLine } from './teamai';

const OUT_MARGIN = PITCH.lineHalfWidth + 0.11;

/** `e_MatchPhase` (`gamedefines.hpp:129-136`). */
export enum MatchPhase {
  PreMatch = 'prematch',
  FirstHalf = 'firsthalf',
  SecondHalf = 'secondhalf',
  FirstExtraTime = 'firstextratime',
  SecondExtraTime = 'secondextratime',
  Penalties = 'penalties',
}

export interface RestartState extends SetPieceSetup {
  stopTimeMs: number;
  prepareTimeMs: number;
  startTimeMs: number;
  prepared: boolean;
  started: boolean;
  taker: MatchPlayer | null;
  /**
   * This kick-off opens a period rather than following a goal — the special row in
   * `referee.cpp:78-111`. It is what starts the match out of the pre-match phase, and it is
   * also why no short whistle is blown for it (`src/audio/state.ts`).
   */
  endPhase: boolean;
  /**
   * A throw-in, or a free kick given for offside — the two restarts real football never lets
   * score on the taker's own strike (Law 15, Law 13). Every other free kick this engine awards
   * comes from `checkFoul`, and every offence that reaches it is a contact one — tripping,
   * holding, a shirt pull, a mistimed charge — which real football treats as direct. See
   * `indirectRestartTaker`.
   */
  indirect: boolean;
}

interface FoulState {
  victim: MatchPlayer;
  tripper: MatchPlayer;
  type: 1 | 2 | 3;
  timeMs: number;
  position: Vector3;
  advantage: boolean;
  processed: boolean;
}

/** A booking the referee has earned the right to show but has not shown yet. */
interface OwedCard {
  player: MatchPlayer;
  /** 2 is a yellow, 3 a red — the two foul severities that carry a card. */
  type: 2 | 3;
}

/**
 * Where one of the three men in the middle is standing, and which way he is looking.
 *
 * Pushed in every tick by `tickOfficial` (`src/sim/officials.ts`), which is the only thing that
 * knows an official has a body at all: the rules referee below is a set of rules and owns no
 * position of its own. `kind` is the same union `officials.ts` exports, written out again here
 * so that nothing in the rules has to import the bodies — `match.ts` imports this file, so an
 * import the other way would close a cycle.
 */
export interface OfficialViewpoint {
  kind: 'referee' | 'linesman';
  position: Vector3;
  facing: Vector3;
}

/**
 * One offence the rules would have punished, and what the referee made of it.
 *
 * Written for every offence that reaches the judgment — the ones he gave and the ones he
 * missed — and read by nothing in the game. It exists because a match total cannot test this
 * feature: fouls here already run about twice below real football's rate, so a referee who
 * misses fouls can only push a broken number further down, and "fouls fell, so his eyesight
 * works" proves nothing. The claim being made is about distance and sight line, and this is
 * the only thing that measures it. See `.agent/scratch/` harnesses and the entry in
 * `PENDING.md`.
 */
export interface OffenceSighting {
  timeMs: number;
  offenderId: number;
  victimId: number;
  /** Which route the offence arrived by, since they are judged by different rules. */
  source: 'standing' | 'sliding' | 'violent' | 'notice';
  /** The severity the rules settled on: 1 no card, 2 a caution, 3 a dismissal. */
  type: 1 | 2 | 3;
  /** Where it happened, so a report can say which part of the pitch he is blind in. */
  position: Vector3;
  /** Metres from the referee's own body, or -1 when no body has been registered. */
  refereeDistance: number;
  /** Degrees between where he was looking and where it happened, or -1. */
  refereeAngle: number;
  /** Metres from the nearer of the two linesmen, or -1. */
  linesmanDistance: number;
  /** What the sight model gave it, 0 to 1. Exactly 1 for a referee with no body. */
  chance: number;
  given: boolean;
}

/**
 * HOW FAR THE REFEREE CAN SEE, and it is the whole of the feature the owner asked for:
 * "cuando uno está lejos el árbitro no ve la mayoría de las veces por lo que puede pegar
 * impunemente".
 *
 * Nothing in the original models this — its referee is a body that walks about and a set of
 * rules that knows everything, and the two never speak. These numbers are therefore chosen
 * rather than ported, and they were chosen against a measured distribution rather than by
 * taste. Over 24 matches, of the 109 offences that reached this judgment, the referee stood a
 * median 16.8 m away, three quarters of them inside 20 m and ninety-nine hundredths inside
 * 29.4 m. The median one was 22 degrees off the way his body was pointed and nine in ten were
 * inside 84 degrees of it, because he tracks the ball and almost every foul in a
 * computer-against-computer match happens at the ball.
 *
 * SO THE CLEAR-SIGHT RADIUS IS SET BEYOND THE WHOLE OF THAT DISTRIBUTION on purpose. Ordinary
 * football is refereed as it always was, and what this changes is the offence committed away
 * from the play — which is the thing the bench cannot produce and a person can.
 *
 * The falloff is linear rather than a curve because there is nothing to fit it to; a curve
 * would be a false precision.
 */
const REFEREE_CLEAR_SIGHT_M = 25;
/**
 * Beyond this he never sees anything, wherever he is looking. Chosen so that a referee near
 * one penalty area cannot referee the other one: the pitch is 110 m long.
 */
const REFEREE_BLIND_M = 45;
/**
 * Half-angle, in degrees, of the arc in which he misses nothing. He is looking at the ball
 * (`refereeCommand`'s `desiredLookAt`), so this is measured off the ball, not off the goal.
 *
 * Wide, and deliberately so: what this compares against is the direction of his TRUNK, and a
 * referee reads play with his head and his eyes. The trunk is a poor proxy for where a man is
 * looking and a good one for what is flatly behind him, so the arc is generous until it turns
 * into "his back was to it".
 */
const REFEREE_CLEAR_ARC_DEG = 90;
/** And this is where his sight line runs out altogether — an offence behind his back. */
const REFEREE_BLIND_ARC_DEG = 160;
/**
 * Close enough that where he was looking stops mattering.
 *
 * A man chopped down two metres behind a referee is heard, felt and complained about, and a
 * model in which that is invisible is worse football than one with no model at all. This is a
 * floor under the arc term, fading to nothing over ten metres, and it is why the sight rule
 * cannot make the referee blind to something happening beside him.
 */
const REFEREE_EARSHOT_M = 10;

/**
 * A linesman is the reason an offence by the far touchline is given while the referee is at
 * the other end. He stands 0.80 m OUTSIDE his touchline looking square across the pitch
 * (`linesmanCommand`), so the whole field is in front of him and there is no arc term to
 * apply: what limits him is how far up the line the thing happened, and that he will only
 * raise a flag for what is plainly his.
 */
const LINESMAN_CLEAR_SIGHT_M = 18;
const LINESMAN_BLIND_M = 38;

/**
 * How long a missed offence stays missed.
 *
 * A held shirt is one offence and sixty ticks of it: the drag is applied every tick the key is
 * down and the referee is told every tick (`applyShirtPull`, `src/input/player.ts`). Roll the
 * dice on each of those and a one-in-twenty chance of being seen becomes a certainty inside a
 * second, and the feature does not exist. So a miss is remembered for the pair of men involved
 * and refreshed for as long as the notices keep coming — he did not see this offence, not he
 * did not see this tick of it.
 */
const UNSEEN_MEMORY_MS = 1000;

/** Salt for `hashedRandom`, distinct from every other draw in the simulation. */
const SIGHT_HASH_SALT = 0x5ee;

/**
 * A restart has to be taken ON THE PITCH, and for two of them this was never said.
 *
 * A player is routinely shoved past a touchline — the collision separation pass alone does it,
 * and a man chasing a ball out of play runs there under his own steam. Both the free kick for a
 * foul (`recordFoul`, the victim's own position) and the free kick for offside (the position
 * where the offside was recorded) took that position as given, so either could be awarded from
 * outside the field of play. A restart out there HANGS THE MATCH FOR EVER: the taker is placed
 * beside the ball, plays a movement clip and never strikes it, and the football clock does not
 * advance while play is stopped, so nothing ever un-stops it.
 *
 * Caught by the stall alarm rather than by anybody playing: FC Bavaria v Rood Wit Eindhoven,
 * seed 7017, a free kick at (46.65, 36.75) against a touchline at 36.0, the clock frozen at
 * 20:44. Clamping only the foul's position did NOT cure it — the offside path is a second,
 * separate route to the same place, which is why both go through here now.
 *
 * The margin is the throw-in's own (`:312`), so a restart on the line lands exactly where a
 * throw-in from the same spot would.
 */
function onThePitch(position: Vector3): Vector3 {
  return new Vector3(
    clamp(position.x, -PITCH.halfLength + 0.6, PITCH.halfLength - 0.6),
    clamp(position.y, -PITCH.halfWidth + 0.6, PITCH.halfWidth - 0.6),
    0,
  );
}

export class Referee {
  restart: RestartState | null = null;
  afterSetPieceRelaxTimeMs = 0;

  /**
   * Does a level match play on past ninety minutes?
   *
   * No, and nothing in the game can say otherwise yet: the only fixture that exists is a
   * friendly, and a friendly still level after ninety minutes is a draw. Extra time belongs to
   * a knockout tie, and a knockout tie needs a shootout behind it, which does not exist either.
   * The extra periods, their clock offsets and the ends the sides change over are all still
   * here and still tested, so a cup can switch this on the day there is a cup.
   */
  extraTime = false;

  /**
   * Every offence that reaches the judgment, given or missed, handed to whoever set this.
   *
   * Null in the game and in the bench's ordinary runs; a measuring harness sets it. See
   * `OffenceSighting`.
   */
  sightLog: ((offence: OffenceSighting) => void) | null = null;

  /**
   * The offender of the most recent card actually shown, and when — so the referee's own body
   * (`refereeCommand`, `src/sim/officials.ts`) can walk over and play the show-card clip.
   * Purely presentational: nothing in this file ever reads it back, and the ten-second
   * ceremony `schedule` already pays for does not answer to whether he actually gets there —
   * see the note on `AlterSetPiecePrepareTime` at the top of `officials.ts`.
   */
  cardShown: { player: MatchPlayer; atMs: number } | null = null;

  private readonly previousBallPosition: Vector3;
  private readonly offsidePlayers = new Map<MatchPlayer, Vector3>();
  private foul: FoulState | null = null;
  /** Cards owed from fouls the referee waved play on for. See `deferCard`. */
  private readonly owedCards: OwedCard[] = [];
  /**
   * The three officials' bodies, by player id, or empty when nobody has told him he has one.
   *
   * EMPTY MEANS OMNISCIENT, and that is deliberate rather than a hole. A `Match` can be built
   * without officials — every rules test does, and so did the measuring bench until this
   * feature needed them — and a referee who cannot be told where he is standing has to judge
   * everything, or those matches would quietly play a different game with no symptom. The one
   * thing that must never happen is the game and the bench disagreeing about it, which is why
   * `stepMatch` ticks the officials for both (`src/sim/steploop.ts`).
   */
  private readonly viewpoints = new Map<number, OfficialViewpoint>();
  /** Offender/victim pairs whose offence went unseen, and when that verdict lapses. */
  private readonly unseenUntilMs = new Map<number, number>();
  /**
   * The taker of a live throw-in or offside free kick, until somebody else touches the ball.
   *
   * Real football asks only that the ball be touched by another player before it can count as a
   * goal — a deflection off a defender satisfies it exactly as a pass to a teammate does — so
   * this is cleared by the very next touch from anyone but the player it names. Set in
   * `ballTouched` the moment such a restart's own taker ends it; read in `process` to withhold
   * the goal his own strike would otherwise be credited. Cleared unconditionally by `schedule`
   * too: a new stoppage makes whatever dead ball this belonged to moot, whether or not anybody
   * touched it again first.
   */
  private indirectRestartTaker: MatchPlayer | null = null;

  constructor(ballPosition: Vector3) {
    this.previousBallPosition = ballPosition.clone();
  }

  get setPiece(): SetPiece {
    return this.restart?.type ?? SetPiece.None;
  }

  get taker(): MatchPlayer | null {
    return this.restart?.taker ?? null;
  }

  /** PreMatch enters the same prepare -> start kick-off sequence as every later phase. */
  startMatch(match: Match): void {
    this.schedule(match, SetPiece.KickOff, 0, new Vector3(), 0, true);
  }

  process(match: Match): void {
    if (match.gameOver) return;
    this.afterSetPieceRelaxTimeMs = Math.max(this.afterSetPieceRelaxTimeMs - 10, 0);

    if (this.restart) {
      if (!this.restart.prepared && match.timeMs >= this.restart.prepareTimeMs) {
        // Starting the match is all that is left of the original's end-of-phase branch. It also
        // asked here whether the match was over, three seconds after the whistle and one phase
        // into a period nobody was going to play; the whistle itself answers that now, in
        // `schedulePhaseEnd`.
        if (this.restart.endPhase && match.phase === MatchPhase.PreMatch) {
          match.setPhase(MatchPhase.FirstHalf);
        }
        this.restart.taker = prepareSetPiece(match, this.restart);
        this.restart.prepared = true;
      }
      if (!this.restart.started && match.timeMs >= this.restart.startTimeMs) {
        match.inPlay = true;
        match.inSetPiece = true;
        this.restart.started = true;
      }
      this.previousBallPosition.copy(match.ball.position);
      return;
    }

    if (!match.inPlay || match.inSetPiece) {
      this.previousBallPosition.copy(match.ball.position);
      return;
    }

    const ball = match.ball.position;

    // A period ends when the clock is up AND the ball has come back near the halfway line. The
    // second half of that is the original's (`referee.cpp:81`) and it is good football: a referee
    // does not blow with a cross in the air.
    //
    // BUT IT NEEDS A DEADLINE, and did not have one. It was safe only while play never left the
    // middle of the pitch — which was itself a defect, now fixed, and the two together were a
    // match that ran forty-three minutes past time and never ended. A period that has run two
    // minutes over is blown wherever the ball is; a referee who has waited that long has already
    // waited too long, and a match that will not finish is worse than one that finishes on a
    // throw-in.
    if (phaseHasEnded(match) && (this.nearHalfway(ball) || this.periodIsLate(match))) {
      if (!this.foulPreemptsStoppage(match)) this.schedulePhaseEnd(match);
      this.previousBallPosition.copy(ball);
      return;
    }

    const goalSide = goalCrossingSide(this.previousBallPosition, ball);
    // A throw-in or an offside free kick struck straight in does not count — see
    // `indirectRestartTaker`. Nothing bespoke is needed for what happens instead: the taker's
    // strike is the only touch since the restart, so `lastTouchTeamId` already reads exactly as
    // it would for any other ball the attacking side put into or over the goal line without a
    // goal, and the boundary-exit branch right below already turns that into the correct
    // restart — a goal kick if it beat the defence, a corner if it curled into the taker's own
    // net — without being told anything happened.
    const disallowedGoal = goalSide !== 0 && this.indirectRestartTaker !== null;
    if (goalSide !== 0 && !disallowedGoal) {
      // A goal is the best advantage there is, so an outstanding foul is thrown away rather
      // than re-judged: the goal stands and no free kick follows it. That is the `IsGoalScored`
      // arm of the goal-line branch, `foul.foulType = 0` (`referee.cpp:118-120`). What does not
      // go with it is the booking — the referee shows that at the kick-off.
      this.deferCard(this.foul);
      this.foul = null;
      const scoringTeam = match.teams.find((team) => team.side === -goalSide)!;
      const concedingTeam = match.opponentTeam(scoringTeam.id);
      scoringTeam.score++;
      match.lastGoalTeamId = scoringTeam.id;
      this.schedule(match, SetPiece.KickOff, concedingTeam.id, new Vector3(), 6000);
    } else if (disallowedGoal || Math.abs(ball.x) > PITCH.halfLength + OUT_MARGIN) {
      if (!this.foulPreemptsStoppage(match)) {
        const lastSide = match.team(match.lastTouchTeamId).side;
        const corner = (ball.x > 0 && lastSide > 0) || (ball.x < 0 && lastSide < 0);
        const type = corner ? SetPiece.Corner : SetPiece.GoalKick;
        const position = corner
          ? new Vector3(PITCH.halfLength * lastSide, Math.sign(ball.y) * PITCH.halfWidth, 0)
          : new Vector3(PITCH.halfLength * 0.92 * -lastSide, 0, 0);
        this.schedule(match, type, match.opponentTeam(match.lastTouchTeamId).id, position, 2000);
      }
    } else if (
      this.afterSetPieceRelaxTimeMs === 0 &&
      Math.abs(ball.y) > PITCH.halfWidth + OUT_MARGIN
    ) {
      if (!this.foulPreemptsStoppage(match)) {
        const position = new Vector3(
          clamp(ball.x, -PITCH.halfLength + 0.6, PITCH.halfLength - 0.6),
          Math.sign(ball.y) * PITCH.halfWidth,
          0,
        );
        this.schedule(
          match,
          SetPiece.ThrowIn,
          match.opponentTeam(match.lastTouchTeamId).id,
          position,
          2000,
          false,
          true, // indirect — a throw-in can never itself produce a goal
        );
      }
    }
    // The ball is still on the field, so this is where an advantage that has run its course
    // becomes a free kick, or is cancelled. It sits after the boundary tests rather than
    // before them (`referee.cpp:187`): judging first would let the three-second cancellation
    // fire on the very tick the ball goes out, and the foul would be lost to the throw-in.
    this.checkFoul(match);
    this.previousBallPosition.copy(ball);
  }

  ballTouched(match: Match, player: MatchPlayer): void {
    if (!player.active) return;
    // See `indirectRestartTaker`: satisfied by anyone else's touch, including this one, whatever
    // else it turns out to mean below.
    if (this.indirectRestartTaker && this.indirectRestartTaker !== player) {
      this.indirectRestartTaker = null;
    }
    const restart = this.restart;
    if (restart) {
      if (!restart.started || restart.taker !== player) {
        this.offsidePlayers.clear();
        return;
      }
      this.restart = null;
      match.inSetPiece = false;
      match.inPlay = true;
      this.afterSetPieceRelaxTimeMs = 400;
      this.previousBallPosition.copy(match.ball.position);
      this.foul = null;
      this.indirectRestartTaker = restart.indirect ? player : null;
    }

    const touchingTeam = match.team(player.teamId);
    const defendingTeam = match.opponentTeam(player.teamId);
    const recordedPosition = this.offsidePlayers.get(player);
    if (!restart && !match.inSetPiece && recordedPosition && defendingTeam.players.length > 1) {
      this.offsidePlayers.clear();
      // An outstanding foul outranks the offside and the offside is dropped, exactly as it
      // outranks a boundary restart (`referee.cpp:281-282`, and the bare `break` at :294).
      if (!this.foulPreemptsStoppage(match)) {
        this.schedule(
          match,
          SetPiece.FreeKick,
          defendingTeam.id,
          onThePitch(recordedPosition),
          2000,
          false,
          true, // indirect — this is an offside free kick, real football's one indirect foul
        );
      }
      return;
    }

    this.offsidePlayers.clear();
    // Law 11 exempts a throw-in, a goal kick and a corner alike — none of them can produce an
    // offside. Only the throw-in used to be exempted here, so a corner or a goal kick could
    // flag a striker in the six-yard box exactly as open play would.
    if (
      !match.inPlay ||
      restart?.type === SetPiece.ThrowIn ||
      restart?.type === SetPiece.Corner ||
      restart?.type === SetPiece.GoalKick
    )
      return;

    const line = offsideLine(match, defendingTeam.id, match.mentalImage(0), 0);
    for (const attacker of touchingTeam.players) {
      if (
        attacker !== player &&
        attacker.position.x * touchingTeam.side < line * touchingTeam.side - 0.2
      ) {
        this.offsidePlayers.set(attacker, attacker.position.clone());
      }
    }
  }

  /** A dismissed set-piece taker must be selected again from the active roster. */
  playerDismissed(player: MatchPlayer): void {
    if (this.restart?.taker !== player) return;
    this.restart.taker = null;
    this.restart.prepared = false;
  }

  /** `Match::GameOver` ends any pending referee protocol as well as play itself. */
  /** `referee.cpp:81` — he will not blow with the ball at one end of the pitch. */
  private nearHalfway(ball: Vector3): boolean {
    return ball.x > -10 && ball.x < 10;
  }

  /** Has the referee waited long enough that where the ball is stops mattering? */
  private periodIsLate(match: Match): boolean {
    const limit = PHASE_END_MS[match.phase];
    return limit !== undefined && match.matchTimeMs > limit + PHASE_END_GRACE_MS;
  }

  gameOver(): void {
    this.restart = null;
    this.foul = null;
    this.owedCards.length = 0;
    this.offsidePlayers.clear();
    this.unseenUntilMs.clear();
    this.indirectRestartTaker = null;
    this.cardShown = null;
  }

  /**
   * Tell the rules where one of the three officials' bodies is, this tick.
   *
   * Called by `tickOfficial` for the referee and both linesmen (`src/sim/officials.ts`). The
   * vectors are copied rather than kept, because the caller hands over the live ones off a
   * moving man and they would be rewritten underneath the judgment.
   */
  observeOfficial(
    id: number,
    kind: OfficialViewpoint['kind'],
    position: Vector3,
    facing: Vector3,
  ): void {
    const existing = this.viewpoints.get(id);
    if (existing) {
      existing.position.copy(position);
      existing.facing.copy(facing);
      return;
    }
    this.viewpoints.set(id, { kind, position: position.clone(), facing: facing.clone() });
  }

  /**
   * A foul the referee is told about directly, rather than one he reads off a collision.
   *
   * Everything the original punishes comes out of two men's legs overlapping, which cannot
   * express an offence with no tackle in it. A shirt pulled from behind is exactly that: there
   * is no challenge to judge, only a man being held.
   *
   * One at a time from the same offender — a hand on a shirt is held down for as long as a
   * person likes, and every tick of it is the same single offence.
   */
  offenceNotice(
    match: Match,
    victim: MatchPlayer,
    offender: MatchPlayer,
    type: 1 | 2 | 3,
  ): void {
    if (this.restart) return;
    // In the match where anything goes he does not judge violence at all, and everything that
    // arrives down this route is violence: it is the route for an offence with no challenge in
    // it, which is to say for a man deciding to do something to an opponent. The football
    // underneath is refereed exactly as before — a mistimed tackle is still a foul in either
    // mode — so the two branches below are left alone. (`Match.violence`, `src/sim/match.ts`.)
    if (match.violence === 'anything-goes') return;
    if (this.foul && !this.foul.processed) return;
    if (offender.teamId === victim.teamId) return;
    if (!this.sawOffence(match, victim, offender, 'notice', type)) return;
    this.recordFoul(match, victim, offender, type, type !== 3);
  }

  tripNotice(
    match: Match,
    victim: MatchPlayer,
    tripper: MatchPlayer,
    tackleType: number,
  ): void {
    if (this.restart) return;

    // A FLYING KICK IS NOT JUDGED, IT IS SENT OFF. Every other challenge in this game is scored
    // on whether the boot got the ball and how far off it was aimed, because every other
    // challenge is at least an attempt at the ball. This one is a man launching himself at
    // another man, and the only thing the referee has to establish is that it connected.
    if (tripper.violentChallengeUntilMs > match.timeMs && tripper.teamId !== victim.teamId) {
      // Consumed whatever happens next, including in the mode where it is not judged and by a
      // referee who was looking the other way. The kick has been thrown; the flag says this
      // challenge was a violent one, and leaving it set would hand the same red card to the
      // next ordinary trip inside the window.
      tripper.violentChallengeUntilMs = 0;
      if (match.violence === 'anything-goes') return;
      if (!this.sawOffence(match, victim, tripper, 'violent', 3)) return;
      this.recordFoul(match, victim, tripper, 3, false);
      return;
    }

    const ballPosition = match.ball.predict(0).setZ(0);
    if (
      tackleType === 2 &&
      match.team(victim.teamId).fadingPossessionAmount > 1.1 &&
      (tripper.anim.currentFunctionType === 'interfere' ||
        tripper.anim.currentFunctionType === 'sliding') &&
      victim.position.distanceTo(ballPosition) < 2 &&
      tripper.teamId !== victim.teamId
    ) {
      // HOW BAD IT WAS, which the original never asks of this challenge — every one of them is
      // its mildest foul (`referee.cpp:331`) and its mildest carries no card. That was invisible
      // while the challenge itself was demoted out of the referee's sight, and the moment it was
      // restored it became the plainest thing wrong with the refereeing: a match with fouls in it
      // and no bookings at all. So it is judged by the same measure the sliding tackle is judged
      // by — did he play the ball, how far from it was his foot, and did he come from behind.
      //
      // THE THRESHOLD IS CHOSEN AGAINST REAL FOOTBALL, not against the formula. Measured over
      // four matches, the severity of these challenges runs from 0.7 to 1.9 with the middle at
      // 1.16; a real referee books roughly one foul in six, and 1.6 is the line that leaves
      // about one in six above it. Nothing here reaches the 2.0 that makes a sending-off, and
      // that is deliberate rather than an oversight: a man who goes to ground studs-first is
      // sliding, and that is the branch below.
      const severity = challengeSeverity(victim, tripper, ballPosition);
      const type = severity > 1.6 ? 2 : 1;
      if (!this.sawOffence(match, victim, tripper, 'standing', type)) return;
      this.recordFoul(match, victim, tripper, type, true);
      return;
    }

    if (
      tackleType !== 3 ||
      this.foul?.tripper === tripper ||
      tripper.lastTouchBias(600, match.timeMs) > 0 ||
      tripper.anim.currentFunctionType !== 'sliding' ||
      tripper.teamId === victim.teamId ||
      victim.position.distanceTo(ballPosition) >= 8
    ) {
      return;
    }

    const severity = challengeSeverity(victim, tripper, ballPosition);
    if (severity <= 1) return;

    const type = severity > 2 ? 3 : severity > 1.4 ? 2 : 1;
    if (!this.sawOffence(match, victim, tripper, 'sliding', type)) return;
    this.recordFoul(match, victim, tripper, type, type !== 3);
  }

  /**
   * Did the referee see it? The one place the rules answer to where the man is standing.
   *
   * Called for every offence the rules have already decided to punish, and for nothing else,
   * so that what it reports is exactly the claim being made: of the offences that were fouls,
   * this share was given and that share was not. It is applied to BOTH SIDES and to a person
   * and the computer alike — gate it on who committed it and it is a cheat one way and a
   * handicap the other.
   *
   * Three officials are asked and the best-placed one decides. Whether he then sees it is a
   * draw against a hash of the tick and the two men (`hashedRandom`, `src/core/random.ts`),
   * never the platform's generator, so a fixture replays exactly. A miss is remembered for the
   * pair for a second and refreshed while the notices keep arriving — see `UNSEEN_MEMORY_MS`,
   * without which a held shirt is sixty separate chances to be spotted.
   */
  private sawOffence(
    match: Match,
    victim: MatchPlayer,
    offender: MatchPlayer,
    source: OffenceSighting['source'],
    type: 1 | 2 | 3,
  ): boolean {
    const at = victim.position;
    const view = this.bestSight(at);
    // Nobody has told him he has a body, so he is the omniscient referee this game had before
    // the officials existed. See `viewpoints`.
    const chance = view ? view.chance : 1;

    const key = offender.id * 64 + victim.id;
    const remembered = this.unseenUntilMs.get(key);
    const roll = hashedRandom(
      Math.round(match.timeMs / 10),
      key,
      SIGHT_HASH_SALT,
      match.randomSeed,
    );
    const given =
      remembered !== undefined && remembered > match.timeMs ? false : chance >= 1 || roll < chance;
    if (!given) this.unseenUntilMs.set(key, match.timeMs + UNSEEN_MEMORY_MS);

    this.sightLog?.({
      timeMs: match.timeMs,
      offenderId: offender.id,
      victimId: victim.id,
      source,
      type,
      position: at.clone(),
      refereeDistance: view?.refereeDistance ?? -1,
      refereeAngle: view?.refereeAngle ?? -1,
      linesmanDistance: view?.linesmanDistance ?? -1,
      chance,
      given,
    });
    return given;
  }

  /**
   * What the three of them between them could see of a point on the pitch.
   *
   * The best-placed official decides rather than the three of them adding up: two men who each
   * half-saw something do not between them make a certainty, and a linesman ten metres from an
   * incident does not see it any better because the referee is also forty metres away.
   */
  private bestSight(at: Vector3): OfficialSight | null {
    if (this.viewpoints.size === 0) return null;
    let chance = 0;
    let refereeDistance = -1;
    let refereeAngle = -1;
    let linesmanDistance = -1;
    for (const view of this.viewpoints.values()) {
      const dx = at.x - view.position.x;
      const dy = at.y - view.position.y;
      const distance = Math.hypot(dx, dy);
      if (view.kind === 'linesman') {
        if (linesmanDistance < 0 || distance < linesmanDistance) linesmanDistance = distance;
        chance = Math.max(chance, falloff(distance, LINESMAN_CLEAR_SIGHT_M, LINESMAN_BLIND_M));
        continue;
      }
      // The referee's own facing is his body's, and his body is pointed at the ball every tick
      // (`refereeCommand`). An offence at the ball is therefore dead ahead of him, which is
      // why ordinary football is refereed as it always was.
      const facing = view.facing;
      const facingLength = Math.hypot(facing.x, facing.y);
      const cosine =
        distance < 1e-6 || facingLength < 1e-6
          ? 1
          : clamp((dx * facing.x + dy * facing.y) / (distance * facingLength), -1, 1);
      refereeDistance = distance;
      refereeAngle = (Math.acos(cosine) * 180) / Math.PI;
      const arc = Math.max(
        falloff(refereeAngle, REFEREE_CLEAR_ARC_DEG, REFEREE_BLIND_ARC_DEG),
        falloff(distance, 0, REFEREE_EARSHOT_M),
      );
      chance = Math.max(chance, falloff(distance, REFEREE_CLEAR_SIGHT_M, REFEREE_BLIND_M) * arc);
    }
    return { chance: clamp(chance, 0, 1), refereeDistance, refereeAngle, linesmanDistance };
  }

  private recordFoul(
    match: Match,
    victim: MatchPlayer,
    tripper: MatchPlayer,
    type: 1 | 2 | 3,
    advantage: boolean,
  ): void {
    // `offenceNotice` already refuses a second foul while the first is live; `tripNotice`'s
    // three branches do not, and an unrelated trip elsewhere on the pitch used to overwrite an
    // unresolved foul outright — losing its card as well as its free kick, since nothing had
    // deferred it first. The free kick is rightly gone, one foul at a time is the original's
    // design, but the card is not: defer whatever this replaces exactly as a stoppage does.
    this.deferCard(this.foul);
    this.foul = {
      victim,
      tripper,
      type,
      timeMs: match.timeMs,
      position: onThePitch(victim.position),
      advantage,
      processed: false,
    };
  }

  /**
   * Stop playing the advantage and judge the foul, answering whether it takes the restart.
   *
   * Every stoppage branch in the original reads `foul.advantage = false; if (!CheckFoul()) {
   * ...award the restart... }` — the goal line (`referee.cpp:118-121`), the touchline
   * (`referee.cpp:168-169`), the end of a period (`referee.cpp:84-85`) and an offside
   * (`referee.cpp:281-282`). Once the ball has left the field the advantage can no longer come
   * to anything, so the free kick, the penalty and the card are collected at that moment
   * instead of being dropped in favour of the throw-in.
   */
  private foulPreemptsStoppage(match: Match): boolean {
    if (this.foul) this.foul.advantage = false;
    return this.checkFoul(match);
  }

  private checkFoul(match: Match): boolean {
    const foul = this.foul;
    if (!foul) return false;
    const victimTeam = match.team(foul.victim.teamId);
    const penalty =
      Math.abs(foul.position.y) < 20.15 - PITCH.lineHalfWidth &&
      foul.position.x * -victimTeam.side >
        PITCH.halfLength - 16.5 + PITCH.lineHalfWidth;

    if (foul.advantage) {
      if (penalty) {
        foul.advantage = false;
      } else if (match.timeMs - 600 > foul.timeMs) {
        if (match.timeMs - 3000 > foul.timeMs) {
          // The advantage ran its course, so there is no free kick to come back for — but the
          // booking outlives the foul. See `deferCard`.
          this.deferCard(foul);
          this.foul = null;
          return false;
        }
        if (victimTeam.fadingPossessionAmount < 1) foul.advantage = false;
      }
    }

    if (foul.advantage || foul.processed) return false;
    const type = penalty ? SetPiece.Penalty : SetPiece.FreeKick;
    const position = penalty
      ? new Vector3((PITCH.halfLength - 11) * match.team(foul.tripper.teamId).side, 0, 0)
      : foul.position;
    // Queued rather than handed over here, so that one place shows cards and one place pays for
    // the ceremony. Since the free kick this foul earns is itself a stoppage, the card still
    // comes out on this very tick, six seconds before it takes effect and ten before the kick.
    this.deferCard(foul);
    foul.processed = true;
    this.schedule(match, type, victimTeam.id, position, 2000);
    return true;
  }

  /**
   * Remember a card the referee has earned the right to show but has not shown yet.
   *
   * A foul can be abandoned for two perfectly good reasons — the advantage was played out, or
   * it ended in a goal — and in both the free kick is rightly thrown away. The booking is not.
   * A real referee waves play on, then comes back at the next stoppage and books the man; the
   * caution is for what he did, and whether his opponents happened to keep the ball for three
   * seconds afterwards has nothing to do with it.
   *
   * The original throws the card away with the foul, and its own author wrote
   * `// todo: yellow cards need to be remembered though ;)` over the line that does it
   * (`referee.cpp:396-398`). This is that todo.
   *
   * "The next stoppage" needed no new machinery: every stoppage in the game is a call to
   * `schedule`, so that is where the debt is paid.
   */
  private deferCard(foul: FoulState | null): void {
    if (!foul || foul.processed || foul.type === 1) return;
    this.owedCards.push({ player: foul.tripper, type: foul.type });
  }

  /**
   * Show every card still owed, and answer whether there was one.
   *
   * A card takes effect six seconds after it is shown, which is the original's allowance for
   * the referee walking over and holding it up (`referee.cpp:432,436`, and its own comment
   * there is "need to find out proper moment"). A second yellow therefore takes the man off
   * six seconds later through the ordinary dismissal path, whether it was shown at the foul or
   * at a throw-in two minutes afterwards.
   */
  private showCards(match: Match): boolean {
    if (this.owedCards.length === 0) return false;
    const effectiveTimeMs = match.timeMs + 6000;
    for (const card of this.owedCards) {
      if (card.type === 2) card.player.giveYellowCard(effectiveTimeMs);
      else card.player.giveRedCard(effectiveTimeMs);
      // Last one wins when two are owed at the same stoppage, which is rare — the original's
      // own one-foul-at-a-time design (`recordFoul`) means the ordinary game hardly ever owes
      // more than one. The referee's body can only walk to one man at a time either way.
      this.cardShown = { player: card.player, atMs: match.timeMs };
    }
    this.owedCards.length = 0;
    return true;
  }

  private schedule(
    match: Match,
    type: SetPiece,
    teamId: number,
    position: Vector3,
    prepareDelayMs: number,
    endPhase = false,
    indirect = false,
  ): void {
    // Every stoppage in the match funnels through here, which makes this the referee's chance
    // to deal with a card — the one this stoppage's own foul earned, and any he has been
    // carrying since he waved play on. Showing one costs the ten seconds the original spends on
    // the ceremony (`referee.cpp:416,423`), whichever foul it belongs to: a booking at a
    // throw-in holds the throw-in up exactly as a booking at a free kick holds up the kick.
    const ceremonyMs = this.showCards(match) ? 10000 : 0;

    // Play stopping also ends whatever was going on, so an offence he missed is over rather
    // than still being missed: the next one between the same two men is judged afresh. See
    // `UNSEEN_MEMORY_MS`, which is only there to make one continuous offence one verdict.
    this.unseenUntilMs.clear();
    // Whatever dead ball `indirectRestartTaker` belonged to is moot the moment a fresh stoppage
    // is scheduled, whether or not anybody ever touched it again — including when nobody was
    // going to, because the period ended first.
    this.indirectRestartTaker = null;
    const prepareTimeMs = match.timeMs + prepareDelayMs + ceremonyMs;
    this.restart = {
      type,
      teamId,
      position,
      stopTimeMs: match.timeMs,
      prepareTimeMs,
      // Ball live the instant everyone is placed, no further wait on top of it. This used to
      // add another 2000 ms after `prepareTimeMs` with nothing anywhere explaining why, and
      // nothing reads `prepared` and `started` as needing to be two different instants — a
      // person mashing the pass key from the moment of the stoppage got nothing back until
      // that second, silent gap ran out too, indistinguishable from the game not responding
      // (see PENDING.md). The "ball is live" whistle (`src/audio/state.ts`) fires off
      // `started` regardless, so it now blows the moment the referee's own placement is
      // finished, not two seconds after it in dead air.
      startTimeMs: prepareTimeMs,
      prepared: false,
      started: false,
      taker: null,
      endPhase,
      indirect,
    };
    match.inPlay = false;
    match.inSetPiece = false;
    match.ballRetainer = null;
  }

  /**
   * The whistle at the end of a period: either the next period, or full time.
   *
   * WHAT FULL TIME MEANS HERE, AND WHY.
   *
   * The only fixture this game has is a friendly, and a friendly is ninety minutes long. If the
   * sides are level at the end of them, it is a draw — that is the result, not an unfinished
   * match. So the second half ending is the final whistle, whatever the score, and it is blown
   * on the spot rather than three seconds later.
   *
   * That is a deliberate departure from the original, which never asks whether a match is over
   * until it has already advanced the clock and the ends into extra time — the question is
   * asked at the kick-off that opens the next period (`referee.cpp:200-214`), so the phase it
   * reads is the one just entered. Two things fall out of that ordering and both are wrong to
   * watch: a match somebody has won ends about three seconds late, in the middle of a
   * transition into a period that is then abandoned; and a drawn match is never asked about at
   * all, so it always plays on. Two whole periods later it arrives at a penalty shootout that
   * does not exist anywhere in the game, and simply stops — a level match therefore has no
   * result at all, after 120 minutes instead of 90.
   *
   * Extra time is not deleted, only switched off (see `extraTime`). It is the right answer for
   * a knockout tie and the machinery for it works: the periods, where the clock restarts, and
   * which way the sides are kicking are all still exercised by the tests. What a knockout tie
   * would still need is the shootout, and the second period of extra time is where that gap
   * shows: level after 120 minutes, the phase is moved on to penalties so that whoever reports
   * the result can say the match got that far, and then the match stops with nothing decided.
   * Do not switch `extraTime` on for a competition until there is something behind it.
   *
   * The other half of the original's asymmetry is worth keeping deliberately dead: it ends a
   * tie the moment the FIRST period of extra time is over. A side a goal up after 105 minutes
   * has not won anything; the second period is played.
   */
  private schedulePhaseEnd(match: Match): void {
    const endedPhase = match.phase;
    const level = match.team(0).score === match.team(1).score;

    if (endedPhase === MatchPhase.SecondHalf && (!level || !this.extraTime)) {
      this.blowFullTime(match);
      return;
    }
    if (endedPhase === MatchPhase.SecondExtraTime) {
      // Still level after 120 minutes means a shootout there is no code for, so the phase is
      // moved on to record that the tie reached one and the match stops undecided.
      if (level) match.setPhase(nextMatchPhase(endedPhase));
      this.blowFullTime(match);
      return;
    }

    const teamId =
      endedPhase === MatchPhase.FirstHalf || endedPhase === MatchPhase.FirstExtraTime ? 1 : 0;
    match.setPhase(nextMatchPhase(endedPhase));
    this.schedule(match, SetPiece.KickOff, teamId, new Vector3(), 3000, true);
  }

  /**
   * The final whistle: the match is over where it stands.
   *
   * The phase is left alone, the football clock is left alone and nothing is scheduled, so the
   * score, the period and the time on the clock are all the ones the match actually finished
   * on. Any card still owed is shown first, because the whistle is a stoppage like any other
   * and the referee is not going to get another one.
   */
  private blowFullTime(match: Match): void {
    this.showCards(match);
    match.endMatch();
  }
}

/** What the three officials between them could see of one point, and how they were placed. */
interface OfficialSight {
  chance: number;
  refereeDistance: number;
  refereeAngle: number;
  linesmanDistance: number;
}

/** 1 up to `clear`, nothing at `blind`, straight line in between. */
function falloff(value: number, clear: number, blind: number): number {
  if (value <= clear) return 1;
  if (value >= blind) return 0;
  return (blind - value) / (blind - clear);
}

/** How far past a period's own end the referee will wait for the ball to come back to him. */
const PHASE_END_GRACE_MS = 120_000;

function phaseHasEnded(match: Match): boolean {
  const limit = PHASE_END_MS[match.phase];
  return limit !== undefined && match.matchTimeMs > limit;
}

const PHASE_END_MS: Partial<Record<MatchPhase, number>> = {
  [MatchPhase.FirstHalf]: 2_700_000,
  [MatchPhase.SecondHalf]: 5_400_000,
  [MatchPhase.FirstExtraTime]: 6_300_000,
  [MatchPhase.SecondExtraTime]: 7_200_000,
};

function nextMatchPhase(phase: MatchPhase): MatchPhase {
  switch (phase) {
    case MatchPhase.FirstHalf:
      return MatchPhase.SecondHalf;
    case MatchPhase.SecondHalf:
      return MatchPhase.FirstExtraTime;
    case MatchPhase.FirstExtraTime:
      return MatchPhase.SecondExtraTime;
    case MatchPhase.SecondExtraTime:
      return MatchPhase.Penalties;
    default:
      return phase;
  }
}

function goalCrossingSide(previous: Vector3, current: Vector3): -1 | 0 | 1 {
  for (const side of [-1, 1] as const) {
    const plane = (PITCH.halfLength + OUT_MARGIN) * side;
    const crossed = side > 0
      ? previous.x < plane && current.x >= plane
      : previous.x > plane && current.x <= plane;
    if (!crossed || current.x === previous.x) continue;
    const u = (plane - previous.x) / (current.x - previous.x);
    const y = previous.y + (current.y - previous.y) * u;
    const z = previous.z + (current.z - previous.z) * u;
    if (Math.abs(y) <= PITCH.goalHalfWidth && z >= 0 && z <= PITCH.goalHeight) return side;
  }
  return 0;
}

/**
 * How bad a challenge was, on a scale where 1 is the line between a fair one and a foul.
 *
 * Three things go into it, and they are the original's for the sliding tackle
 * (`referee.cpp:346-357`). How far from his own ball contact the man was when he made it: a
 * tackler who has no contact planned at all starts at the full 1, and one who timed his to the
 * frame starts near nought. How far the ball actually was from where he aimed his foot. And
 * whether he came from behind, which adds nought to one on top.
 */
function challengeSeverity(
  victim: MatchPlayer,
  tripper: MatchPlayer,
  ballPosition: Vector3,
): number {
  let severity = 1;
  if (tripper.anim.touchFrame !== -1) {
    const frameDistance =
      Math.abs(tripper.anim.touchFrame - tripper.anim.frameNum) /
      Math.max(tripper.anim.touchFrame, 1);
    severity = Math.pow(clamp(frameDistance, 0, 1), 0.7) * 0.5;
    severity += clamp(ballPosition.distanceTo(tripper.anim.touchPosition) / 2, 0, 1) * 0.5;
  }
  const fromBehind =
    victim.position.clone().sub(tripper.position).normalize().dot(victim.directionVec) * 0.5 + 0.5;
  return severity + fromBehind;
}
